import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react";
import { RCCButton, RCCGrid, RCCHierarchicalTree, RCCRefHierarchicalTree, RCCTooltip, toggleExpandedForAll } from "react-common-components";
import permissionsMenu from "src/context/Auth/permissionsMenu";
import { useTranslation } from "src/context/Translations/context";
import HierarchyNode from "../HierarchyNode";
import { IHierarchyComponent, IRefHierarchyComponent } from "./interface";

const HierarchyContractItems : React.ForwardRefRenderFunction<IRefHierarchyComponent, IHierarchyComponent> = ({
	permissionsData,
	control,
	register
}, ref) => {

	const { translate } = useTranslation();

	const refHierarchicalTree = useRef<RCCRefHierarchicalTree>(null);
	const [hierarchy, setHierarchy] = useState<any[]>(permissionsMenu.filter((x) => x.efb001Codigo !== "systemConfigs").map((x) => ({
		...x,
		children: x.children?.filter((child) => !child.internalAction)
	})));
    
	const [expanded, setExpanded] = useState(false);
	const expandAll = (newHierarchy?: any) => {
		const expandedData = toggleExpandedForAll({treeData: newHierarchy && Array.isArray(newHierarchy) ? [...newHierarchy] : [...hierarchy], expanded: !expanded});
		setHierarchy(expandedData);
		setExpanded(!expanded);
	};

	const compressAll = async (newHierarchy?: any) => {
		const expandedData = toggleExpandedForAll({treeData: newHierarchy && Array.isArray(newHierarchy) ? [...newHierarchy] : [...hierarchy], expanded: false});
		setHierarchy(expandedData);
		setExpanded(false);
	};

	const changeChildrenCheckbox = async (efb001Codigo: string, value: boolean) => {
		let newHierarchy = [...hierarchy];
		newHierarchy = newHierarchy.map((x: any) => {
			if(x.efb001Codigo === efb001Codigo){
				return {
					...x,
					contractHasAccessDefaultValue: value,
					contractHasAccess: value,
					expanded: true,
					children: x.children.map((y: any) => ({
						...y,
						contractHasAccessDefaultValue: value,
						contractHasAccess: value
					}))
				};
			}
			return {...x };
		});
		await compressAll(newHierarchy);
		setHierarchy(newHierarchy);
	};	

	const getNodeKeyFunction = ({node}: any) => node.children && node.children.length > 0 ? `remove_${node.efb001Codigo}` : node.efb001Codigo;
	const HierarchicalTree = useMemo(() => <RCCHierarchicalTree
		data={hierarchy}
		styleInline="width: 100%; .rst__tree{ height: 100%; padding-bottom: 1rem; }"
		ref={refHierarchicalTree}
		isSearchable={{
			searchKeyOfNode: "efb005Descricao",
			placeholderInput: translate("Pesquisar")
		}}
		getNodeKey={getNodeKeyFunction}
		generateNodeProps={(row:any) => ({
			title: <HierarchyNode 
				node={row.node}
				parentNode={row.parentNode}
				control={control}
				register={register}
				permissionsData={permissionsData}
				changeChildrenCheckbox={changeChildrenCheckbox}
			/>
		})}
		canDrop={() => false}
		canDrag={() => false}
	/>, [hierarchy, permissionsData]);

	useImperativeHandle(ref, () => ({
		compress: compressAll,
		reset: () => {
			setHierarchy(permissionsMenu.filter((x) => x.efb001Codigo !== "systemConfigs").map((x) => ({
				...x,
				children: x.children?.filter((child) => !child.internalAction)
			})));
		}
	}));

	return (
		<>
			<RCCGrid container item justifyContent="flex-end" alignItems="center">
				
				<RCCTooltip
					color="grey"
					text={translate("Expandir/Comprimir")}
				>
					<RCCButton 
						color="transparent"
						icon={{
							name: expanded ? "compress" : "expand",
							color: "var(--default-color-font)",
							size: 16
						}}
						onClick={expandAll}
					/>
				</RCCTooltip>
				<RCCTooltip
					color="grey"
					text={translate("Pesquisar")}
				>
					<RCCButton 
						color="transparent"
						icon={{
							name: "search",
							color: "var(--default-color-font)",
							size: 16
						}}
						onClick={() => refHierarchicalTree.current?.toggleSearch()}
					/>
				</RCCTooltip>
			</RCCGrid>					
			
			{HierarchicalTree}
		</>
	);
};

export default forwardRef(HierarchyContractItems);
